import { getBulkCases } from '@/common/services/CaseService'
import { getBulkCustomers } from '@/common/services/CustomerService'
import { getBulkInteractions } from '@/common/services/InteractionService'
import { getBulkCompanies } from '@/common/services/CompanyService'
import { type Case, type CaseDetailsDTOV1, CaseExpandEnum, CaseFieldInjectionMap } from '@/common/types/case'
import { CaseIncludeEnum } from '@/common/types/case'
import { EntityFieldCodeEnum } from '@/common/types/fields'
import { EntityEnum, type FetchCallbackFunctions, EntityFieldInjectionMethods } from '@/common/types/general'
import { type MainEntityExpandKeys } from '@/common/types/mainEntity'
import { type Interaction } from '@/common/types/interaction'
import { ColumnStatusEnum, type ColumnDefinition } from '@/search/types/resultViewConfig'
import { type EntitySearchable } from '@/search/types/search'
import {
  type Customer,
  type CustomerDetailsDTOV1,
  CustomerExpandEnumV3,
  CustomerFieldInjectionMapV3,
  type CustomerIncludeEnumV3,
} from '@/common/types/customer'
import { type Company, CompanyExpandEnum, type CompanyDetailsDTOV1 } from '@/common/types/company'
import { flattenCustomFields } from '@/common/services/FieldService'
import {
  CorrespondingUserDataCaseToUserSummaryMap,
  CorrespondingCaseDetailsToActionDataMap,
  CorrespondingCaseDetailsToInteractionDataMap,
  CorrespondingTeamDataCaseToTeamMap,
  fetchCasesActionsData,
  fetchCasesInteractionsData,
  fetchCasesSkillsData,
  fetchCasesUsersData,
  getInformationToFetchCasesFields,
  type keyOfCase,
  type keyOfCaseDetails,
} from '@/common/services/CaseFetchService'
import { type InteractionIncludeEnum, InteractionExpandEnum, InteractionFieldInjectionMap } from '@/common/types/interactionProperties'

/**
 * Fetch one entity to get details
 * @param entitySelected Entity to fetch
 * @param id entity id
 * @param resultViewConfig listing configuration
 * @returns Promise for one entity
 */
export const fetchEntities = async (
  entitySelected: EntityEnum,
  ids: number[],
  columnsDisplayed: ColumnDefinition[],
): Promise<Array<EntitySearchable>> => {
  if (entitySelected === EntityEnum.TICKET) {
    return fetchCases(ids, columnsDisplayed)
  } else if (entitySelected === EntityEnum.CUSTOMER) {
    return fetchCustomers(ids, columnsDisplayed)
  } else if (entitySelected === EntityEnum.INTERACTION) {
    return fetchInteractions(ids, columnsDisplayed)
  } else if (entitySelected === EntityEnum.COMPANY) {
    return fetchCompanies(ids, columnsDisplayed)
  }
  return []
}

/**
 * FETCH CASES
 */

enum CaseAttributesScopesEnum {
  CasesCustomers,
  CasesCompanies,
  CasesActionsData,
  CasesInteractionsData,
  CasesUsersData,
  CasesSkillsData,
  CasesCustomFields,
}

const fetchCases = async (ids: number[], columnsDisplayed: ColumnDefinition[]): Promise<Array<Case>> => {
  const {
    fieldConfig,
    hasCustomerFields,
    hasCompanyFields,
    hasSkillTargetProcessTimeField,
    keysActionsDataToFetch,
    propertyActionsDataToSetOnCase,
    keysInteractionsDataToFetch,
    propertyInteractionsDataToSetOnCase,
    propertyUserDataToSetOnCase,
    propertyTeamDataToSetOnCase,
    columnsSetByScopes,
  } = prepareInformationsToFetchCaseAndFields(columnsDisplayed)

  const cases: CaseDetailsDTOV1[] = await getBulkCases(ids, fieldConfig)
  resetBasicFieldsColumnStatus(columnsDisplayed, columnsSetByScopes)

  const { customerCases, companyCases, actionDataCases, interactionDataCases, caseUserData } = getInformationToFetchCasesFields(
    cases,
    keysActionsDataToFetch,
    keysInteractionsDataToFetch,
  )

  const columnsCustomer = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesCustomers)
  fetchCasesCustomers(customerCases, hasCustomerFields, columnsCustomer, {
    onSuccess: () => setColumnsStatus(columnsCustomer),
    onFailure: () => setColumnsStatus(columnsCustomer, ColumnStatusEnum.ERROR),
  })

  const columnsCompany = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesCompanies)
  fetchCasesCompanies(companyCases, hasCompanyFields, columnsCompany, {
    onSuccess: () => setColumnsStatus(columnsCompany),
    onFailure: () => setColumnsStatus(columnsCompany, ColumnStatusEnum.ERROR),
  })

  const columnsActions = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesActionsData)
  fetchCasesActionsData(actionDataCases, propertyActionsDataToSetOnCase, {
    onSuccess: () => setColumnsStatus(columnsActions),
    onFailure: () => setColumnsStatus(columnsActions, ColumnStatusEnum.ERROR),
  })

  const columnsInteractions = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesInteractionsData)
  fetchCasesInteractionsData(interactionDataCases, propertyInteractionsDataToSetOnCase, {
    onSuccess: () => setColumnsStatus(columnsInteractions),
    onFailure: () => setColumnsStatus(columnsInteractions, ColumnStatusEnum.ERROR),
  })

  const columnsUsers = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesUsersData)
  fetchCasesUsersData(caseUserData, propertyUserDataToSetOnCase, propertyTeamDataToSetOnCase, {
    onSuccess: () => setColumnsStatus(columnsUsers),
    onFailure: () => setColumnsStatus(columnsUsers, ColumnStatusEnum.ERROR),
  })

  const columnsSkills = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesSkillsData)
  fetchCasesSkillsData(hasSkillTargetProcessTimeField, cases, {
    onSuccess: () => setColumnsStatus(columnsSkills),
    onFailure: () => setColumnsStatus(columnsSkills, ColumnStatusEnum.ERROR),
  })

  const columnsEntityCustomFields = columnsSetByScopes.get(CaseAttributesScopesEnum.CasesCustomFields)
  flattenCustomFieldsForEntitySearchable(fieldConfig.expands, cases, {
    onSuccess: () => setColumnsStatus(columnsEntityCustomFields),
    onFailure: () => setColumnsStatus(columnsEntityCustomFields, ColumnStatusEnum.ERROR),
  })

  return cases
}

const prepareInformationsToFetchCaseAndFields = (columnsDisplayed: ColumnDefinition[]) => {
  const includes: Set<CaseIncludeEnum> = new Set([CaseIncludeEnum.NB_INTERACTIONS])
  // For fetch customers
  let hasCustomerFields: boolean = false
  // For fetch
  let hasCompanyFields: boolean = false
  // For include KEY_ACTIONS_DATA
  const keysActionsDataToFetch: Set<keyOfCaseDetails> = new Set()
  const propertyActionsDataToSetOnCase = new Map<EntityFieldCodeEnum, keyOfCase>([])
  // For include KEY_INTERACTIONS_DATA
  const keysInteractionsDataToFetch: Set<keyOfCaseDetails> = new Set()
  const propertyInteractionsDataToSetOnCase = new Map<EntityFieldCodeEnum, keyOfCase>([])
  // For assignee data and team data properties
  const propertyUserDataToSetOnCase = new Map<EntityFieldCodeEnum, keyOfCase>([])
  const propertyTeamDataToSetOnCase = new Map<EntityFieldCodeEnum, keyOfCase>([])
  // For fetch skills
  let hasSkillTargetProcessTimeField: boolean = false
  // For expand CUSTOM_FIELDS
  const expands: Set<CaseExpandEnum> = new Set()
  // For manage errors on specific columns
  const columnsSetByScopes = new Map<CaseAttributesScopesEnum, ColumnDefinition[]>([])

  for (const column of columnsDisplayed) {
    const nativeFieldCode = column.code as EntityFieldCodeEnum

    // For fetch customers
    if (column.entity === EntityEnum.CUSTOMER) {
      if (column.code !== EntityFieldCodeEnum.CUSTOMER_ID) {
        hasCustomerFields = true
      }
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesCustomers, column)
    }

    // For fetch companies
    if (column.entity === EntityEnum.COMPANY) {
      if (column.code !== EntityFieldCodeEnum.COMPANY_ID) {
        hasCompanyFields = true
      }
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesCompanies, column)
    }

    addInformationsAboutIncludes(
      column,
      nativeFieldCode,
      includes,
      keysActionsDataToFetch,
      propertyActionsDataToSetOnCase,
      keysInteractionsDataToFetch,
      propertyInteractionsDataToSetOnCase,
      columnsSetByScopes,
    )

    // For assignee data and team data properties
    if (CorrespondingUserDataCaseToUserSummaryMap.has(nativeFieldCode)) {
      propertyUserDataToSetOnCase.set(nativeFieldCode, column.property as keyOfCase)
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesUsersData, column)
    } else if (CorrespondingTeamDataCaseToTeamMap.has(nativeFieldCode)) {
      propertyTeamDataToSetOnCase.set(nativeFieldCode, column.property as keyOfCase)
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesUsersData, column)
    }

    // For fetch skills
    if (nativeFieldCode === EntityFieldCodeEnum.SKILL_TARGET_PROCESSING_TIME) {
      hasSkillTargetProcessTimeField = true
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesSkillsData, column)
    }

    // For expand CUSTOM_FIELDS
    if (column.entity === EntityEnum.TICKET && column.custom) {
      expands.add(CaseExpandEnum.CUSTOM_FIELDS)
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesCustomFields, column)
    }
  }

  const fieldConfig = {
    includes,
    expands,
  }

  return {
    fieldConfig,
    hasCustomerFields,
    hasCompanyFields,
    hasSkillTargetProcessTimeField,
    keysActionsDataToFetch,
    propertyActionsDataToSetOnCase,
    keysInteractionsDataToFetch,
    propertyInteractionsDataToSetOnCase,
    propertyUserDataToSetOnCase,
    propertyTeamDataToSetOnCase,
    columnsSetByScopes,
  }
}

const addInformationsAboutIncludes = (
  column: ColumnDefinition,
  nativeFieldCode: EntityFieldCodeEnum,
  includes: Set<CaseIncludeEnum>,
  keysActionsDataToFetch: Set<keyOfCaseDetails>,
  propertyActionsDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  keysInteractionsDataToFetch: Set<keyOfCaseDetails>,
  propertyInteractionsDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  columnsSetByScopes: Map<CaseAttributesScopesEnum, ColumnDefinition[]>,
) => {
  if (CaseFieldInjectionMap.has(nativeFieldCode)) {
    const injection = CaseFieldInjectionMap.get(nativeFieldCode)!
    if (injection.method !== EntityFieldInjectionMethods.INCLUDE) {
      return
    }

    const include = injection.scope as CaseIncludeEnum

    includes.add(include)

    // For include KEY_ACTIONS_DATA
    if (CorrespondingCaseDetailsToActionDataMap.has(nativeFieldCode) && include === CaseIncludeEnum.KEY_ACTIONS_DATA) {
      keysActionsDataToFetch.add(CorrespondingCaseDetailsToActionDataMap.get(nativeFieldCode)![0])
      propertyActionsDataToSetOnCase.set(nativeFieldCode, column.property as keyOfCase)
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesActionsData, column)
    }
    // For include KEY_INTERACTIONS_DATA
    else if (CorrespondingCaseDetailsToInteractionDataMap.has(nativeFieldCode) && include === CaseIncludeEnum.KEY_INTERACTIONS_DATA) {
      keysInteractionsDataToFetch.add(CorrespondingCaseDetailsToInteractionDataMap.get(nativeFieldCode)![0])
      propertyInteractionsDataToSetOnCase.set(nativeFieldCode, column.property as keyOfCase)
      addColumnForScope(columnsSetByScopes, CaseAttributesScopesEnum.CasesInteractionsData, column)
    }
  }
}

const fetchCasesCustomers = async (
  customerCases: Map<number, Case[]>,
  hasCustomerFields: boolean,
  columnsCustomer?: ColumnDefinition[],
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  if (!customerCases.size) {
    onSuccess?.()
    return
  }

  try {
    const customers = hasCustomerFields
      ? await fetchCustomers([...customerCases.keys()], columnsCustomer!)
      : [...customerCases.keys()].map((id) => ({ id }))
    customers.forEach((customer) => {
      const caseList = customerCases.get(customer.id) ?? []
      caseList.forEach((c) => {
        c.customer = customer
      })
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

const fetchCasesCompanies = async (
  companyCases: Map<number, Case[]>,
  hasCompanyFields: boolean,
  columnsCompany?: ColumnDefinition[],
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  if (!companyCases.size) {
    onSuccess?.()
    return
  }

  try {
    const companies = hasCompanyFields
      ? await fetchCompanies([...companyCases.keys()], columnsCompany!)
      : [...companyCases.keys()].map((id) => ({ id }))
    companies.forEach((company) => {
      const caseList = companyCases.get(company.id) ?? []
      caseList.forEach((c) => {
        c.company = company
      })
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

/**
 * FETCH CUSTOMERS
 */

enum CustomerAttributesScopesEnum {
  CustomersCustomFields,
}

const fetchCustomers = async (ids: number[], columnsDisplayed: ColumnDefinition[]): Promise<Array<Customer>> => {
  const { fieldConfig, columnsSetByScopes } = prepareInformationsToFetchCustomerAndFields(columnsDisplayed)

  const customers: CustomerDetailsDTOV1[] = await getBulkCustomers(ids, fieldConfig)
  resetBasicFieldsColumnStatus(columnsDisplayed, columnsSetByScopes)

  const columnsEntityCustomFields = columnsSetByScopes.get(CustomerAttributesScopesEnum.CustomersCustomFields)
  flattenCustomFieldsForEntitySearchable(fieldConfig.expands, customers, {
    onSuccess: () => setColumnsStatus(columnsEntityCustomFields),
    onFailure: () => setColumnsStatus(columnsEntityCustomFields, ColumnStatusEnum.ERROR),
  })

  return customers
}

const prepareInformationsToFetchCustomerAndFields = (columnsDisplayed: ColumnDefinition[]) => {
  const includes = new Set<CustomerIncludeEnumV3>([])
  const expands = new Set<CustomerExpandEnumV3>([])
  // For manage errors on specific columns
  const columnsSetByScopes = new Map<CustomerAttributesScopesEnum, ColumnDefinition[]>([])

  for (const column of columnsDisplayed) {
    const nativeFieldCode = column.code as EntityFieldCodeEnum

    if (CustomerFieldInjectionMapV3.has(nativeFieldCode)) {
      const injection = CustomerFieldInjectionMapV3.get(nativeFieldCode)!
      if (injection.method === EntityFieldInjectionMethods.INCLUDE) {
        includes.add(injection.scope as CustomerIncludeEnumV3)
      } else {
        expands.add(injection.scope as CustomerExpandEnumV3)
      }
    }

    // For expand CUSTOM_FIELDS
    if (column.entity === EntityEnum.CUSTOMER && column.custom) {
      expands.add(CustomerExpandEnumV3.CUSTOM_FIELDS)
      addColumnForScope(columnsSetByScopes, CustomerAttributesScopesEnum.CustomersCustomFields, column)
    }
  }

  const fieldConfig = {
    includes,
    expands,
  }

  return {
    fieldConfig,
    columnsSetByScopes,
  }
}

/**
 * FETCH INTERACTIONS
 */

enum InteractionAttributesScopesEnum {
  InteractionsCases,
  InteractionsCustomFields,
}

const fetchInteractions = async (ids: number[], columnsDisplayed: ColumnDefinition[]): Promise<Array<Interaction>> => {
  const { fieldConfig, hasCaseFields, columnsSetByScopes } = prepareInformationsToFetchInteractionAndFields(columnsDisplayed)

  const interactions: Interaction[] = await getBulkInteractions(ids, fieldConfig)
  resetBasicFieldsColumnStatus(columnsDisplayed, columnsSetByScopes)

  const { caseInteractions } = getInformationsToFetchInteractionsFields(interactions)

  const columnsCase = columnsSetByScopes.get(InteractionAttributesScopesEnum.InteractionsCases)
  fetchInteractionsCases(caseInteractions, hasCaseFields, columnsCase, {
    onSuccess: () => setColumnsStatus(columnsCase),
    onFailure: () => setColumnsStatus(columnsCase, ColumnStatusEnum.ERROR),
  })

  const columnsEntityCustomFields = columnsSetByScopes.get(InteractionAttributesScopesEnum.InteractionsCustomFields)
  flattenCustomFieldsForEntitySearchable(fieldConfig.expands, interactions, {
    onSuccess: () => setColumnsStatus(columnsEntityCustomFields),
    onFailure: () => setColumnsStatus(columnsEntityCustomFields, ColumnStatusEnum.ERROR),
  })

  return interactions
}

const prepareInformationsToFetchInteractionAndFields = (columnsDisplayed: ColumnDefinition[]) => {
  const includes: Set<InteractionIncludeEnum> = new Set()
  const expands: Set<InteractionExpandEnum> = new Set()
  // For fetch cases
  let hasCaseFields: boolean = false
  // For manage errors on specific columns
  const columnsSetByScopes = new Map<InteractionAttributesScopesEnum, ColumnDefinition[]>([])

  for (const column of columnsDisplayed) {
    const nativeFieldCode = column.code as EntityFieldCodeEnum

    // For fetch customers
    if (column.entity === EntityEnum.TICKET) {
      if (column.code !== EntityFieldCodeEnum.TICKET_ID) {
        hasCaseFields = true
      }
      addColumnForScope(columnsSetByScopes, InteractionAttributesScopesEnum.InteractionsCases, column)
    }

    if (InteractionFieldInjectionMap.has(nativeFieldCode)) {
      const injection = InteractionFieldInjectionMap.get(nativeFieldCode)!
      if (injection.method === EntityFieldInjectionMethods.INCLUDE) {
        const include = injection.scope as InteractionIncludeEnum
        includes.add(include)
      }
    }

    // For expand CUSTOM_FIELDS
    if (column.entity === EntityEnum.INTERACTION && column.custom) {
      expands.add(InteractionExpandEnum.CUSTOM_FIELDS)
      addColumnForScope(columnsSetByScopes, InteractionAttributesScopesEnum.InteractionsCustomFields, column)
    }
  }

  const fieldConfig = {
    includes,
    expands,
  }

  return {
    fieldConfig,
    hasCaseFields,
    columnsSetByScopes,
  }
}

const getInformationsToFetchInteractionsFields = (interactions: Interaction[]) => {
  return interactions.reduce<{
    caseInteractions: Map<number, Interaction[]>
  }>(
    (accumulator, interactionData) => {
      if (interactionData.caseId) {
        if (accumulator.caseInteractions.has(interactionData.caseId)) accumulator.caseInteractions.get(interactionData.caseId)?.push(interactionData)
        else accumulator.caseInteractions.set(interactionData.caseId, [interactionData])
      }

      return accumulator
    },
    {
      caseInteractions: new Map<number, Interaction[]>(),
    },
  )
}

const fetchInteractionsCases = async (
  caseInteractions: Map<number, Interaction[]>,
  hasCaseFields: boolean,
  columnsCase?: ColumnDefinition[],
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  if (!caseInteractions.size) {
    onSuccess?.()
    return
  }

  try {
    const cases = hasCaseFields ? await fetchCases([...caseInteractions.keys()], columnsCase!) : [...caseInteractions.keys()].map((id) => ({ id }))

    cases.forEach((oneCase) => {
      const interactionList = caseInteractions.get(oneCase.id) ?? []
      interactionList.forEach((i) => {
        i.case = oneCase as Case
      })
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

/**
 * FETCH COMPANIES
 */

enum CompanyAttributesScopesEnum {
  CompaniesCustomFields,
}

const fetchCompanies = async (ids: number[], columnsDisplayed: ColumnDefinition[]): Promise<Array<Company>> => {
  const { fieldConfig, columnsSetByScopes } = prepareInformationsToFetchCompanyAndFields(columnsDisplayed)

  const companies: CompanyDetailsDTOV1[] = await getBulkCompanies(ids, fieldConfig)
  resetBasicFieldsColumnStatus(columnsDisplayed, columnsSetByScopes)

  const columnsEntityCustomFields = columnsSetByScopes.get(CompanyAttributesScopesEnum.CompaniesCustomFields)
  flattenCustomFieldsForEntitySearchable(fieldConfig.expands, companies, {
    onSuccess: () => setColumnsStatus(columnsEntityCustomFields),
    onFailure: () => setColumnsStatus(columnsEntityCustomFields, ColumnStatusEnum.ERROR),
  })

  return companies
}

const prepareInformationsToFetchCompanyAndFields = (columnsDisplayed: ColumnDefinition[]) => {
  const expands = new Set<CompanyExpandEnum>()
  const columnsSetByScopes = new Map<CompanyAttributesScopesEnum, ColumnDefinition[]>([])

  for (const column of columnsDisplayed) {
    // For expand CUSTOM_FIELDS
    if (column.entity === EntityEnum.COMPANY && column.custom) {
      expands.add(CompanyExpandEnum.CUSTOM_FIELDS)
      addColumnForScope(columnsSetByScopes, CompanyAttributesScopesEnum.CompaniesCustomFields, column)
    }
  }

  const fieldConfig = {
    expands,
  }

  return {
    fieldConfig,
    columnsSetByScopes,
  }
}

type EntityAttributesScopesEnum =
  CaseAttributesScopesEnum | InteractionAttributesScopesEnum | CustomerAttributesScopesEnum | CompanyAttributesScopesEnum

const addColumnForScope = (
  columnsSetByScopes: Map<EntityAttributesScopesEnum, ColumnDefinition[]>,
  scope: EntityAttributesScopesEnum,
  column: ColumnDefinition,
) => {
  if (!columnsSetByScopes.has(scope)) {
    columnsSetByScopes.set(scope, [])
  }
  const columns = columnsSetByScopes.get(scope)
  columnsSetByScopes.set(scope, [...columns!, column])
}

const flattenCustomFieldsForEntitySearchable = (
  expands: Set<MainEntityExpandKeys>,
  entities: EntitySearchable[],
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  try {
    if (expands.has('CUSTOM_FIELDS')) {
      flattenCustomFields(entities)
    }
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

const resetBasicFieldsColumnStatus = (
  columnsDisplayed: ColumnDefinition[],
  columnsSetByScopes: Map<EntityAttributesScopesEnum, ColumnDefinition[]>,
) => {
  const allColumnsSetByScopeSet = new Set<string>()
  ;[...columnsSetByScopes].forEach((scope) => scope[1].forEach((column) => allColumnsSetByScopeSet.add(column.code)))

  columnsDisplayed.forEach((column) => {
    if (allColumnsSetByScopeSet.has(column.code)) return
    column.status = undefined
  })
}

export const setColumnsStatus = (columns?: ColumnDefinition[], status?: ColumnStatusEnum) => {
  columns?.forEach((column) => (column.status = status))
}
